import Icon from "@/v2-ui/icon"
import { toast, errorToast } from "@/v2-ui/toast"
import {
  ConfirmButton,
  TooltipButton,
} from "@/v2-backoffice-client/components/button"
import { DotText } from "@/v2-backoffice-client/components/dot"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import { useTenantActivationResendMutation } from "@/v2-backoffice-client/tenant/migration/tenant.migration.resend.mutation"
import TenantActivationStatusBadge from "@/v2-backoffice-client/tenant/migration/TenantActivationStatusBadge"
import {
  canSendActivation,
  getActivationOutcomeStyle,
  type ActivationRunOutcome,
} from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"
import type { TenantUserActivationFragment } from "@/v2-backoffice-client/tenant/migration/__types__/tenant.migration.fragment"

export type MigrationCenterMemberRowProps = {
  tenantId: number
  row: TenantUserActivationFragment
  /** This member's result from the last send in this session. */
  outcome?: ActivationRunOutcome
  onOutcome: (userId: number, outcome: ActivationRunOutcome) => void
  /** Removes this member from the center's lists AND every send button's
   *  target set — the quick "not this one" while reviewing a wave. */
  onExclude(userId: number): void
  /** Any send changes board state — the host refetches the boards. */
  onChanged?(): void
}

/**
 * One member row of the Migration Center's team tables: identity, send
 * stats, live status, this session's outcome, and the per-member send.
 * Owning the send mutation here keeps its loading state scoped to THIS
 * row. The send runs the wave's single-user path (fresh sends and renewals
 * alike), so per-member sends can never drift from waves.
 */
function MigrationCenterMemberRow(props: MigrationCenterMemberRowProps) {
  const { tenantId, row, outcome, onOutcome, onExclude, onChanged } = props

  const [sendActivation, { loading: sending, error: sendError }] =
    useTenantActivationResendMutation()
  errorToast(sendError, { title: "Failed to send the activation" })

  async function send() {
    const result = await sendActivation({
      variables: { tenantId, userId: row.UserId },
    })
    // errorPolicy is "all" app-wide, so a rejected send resolves instead
    // of throwing — the errorToast above carries the reason.
    if (result.errors?.length) return
    const sent = result.data?.resendUserActivation
    if (!sent) return

    onOutcome(row.UserId, { outcome: sent.Outcome, error: sent.Error })
    toast({
      title: "Activation sent",
      description: getActivationOutcomeStyle(sent.Outcome).label,
    })
    onChanged?.()
  }

  const outcomeStyle = outcome
    ? getActivationOutcomeStyle(outcome.outcome)
    : null
  const sendable = canSendActivation(row.Status)

  return (
    <tr className="border-b border-border/60 last:border-b-0">
      <td className="min-w-0 px-3 py-1">
        <p className="truncate text-sm">
          {formatUserName(row.FirstName, row.LastName) ||
            row.Email ||
            `User #${row.UserId}`}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {row.Email ?? "No email — cannot be migrated"}
        </p>
      </td>
      <td className="whitespace-nowrap px-3 py-1">
        {/* The target-group signal: has this member actually used v2, or
            are they a v1-only veteran the wave should think twice about. */}
        <DotText
          className="text-xs"
          variant={row.UsesV2 ? "success-1" : "muted"}
        >
          {row.UsesV2 ? "V2" : "V1"}
        </DotText>
      </td>
      <td className="whitespace-nowrap px-3 py-1 text-xs text-muted-foreground">
        {row.ActivationSentCount != null && row.ActivationSentCount > 0 && (
          <>
            {row.ActivationSentCount}×
            {row.ActivationLastSentAt && (
              <>
                {", "}
                <RelativeDate date={row.ActivationLastSentAt} />
              </>
            )}
          </>
        )}
      </td>
      <td className="px-3 py-1">
        <span className="flex items-center gap-2">
          <TenantActivationStatusBadge
            className="w-36 shrink-0"
            status={row.Status}
            experience={row.Experience}
          />
          {outcomeStyle && (
            <span
              className="inline-flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground"
              title={outcome?.error ?? undefined}
            >
              <Icon
                className={`shrink-0 ${outcomeStyle.iconColor}`}
                src={outcomeStyle.iconSrc}
                size={14}
              />
              <span className="shrink-0">{outcomeStyle.label}</span>
              {/* The reason a member failed used to live only in the title
                  attribute, i.e. behind a hover nobody knew to do; the wave
                  summary then just said "4 failed". Inline, truncated, the
                  full text still on hover. */}
              {outcome?.error && (
                <span className="truncate text-destructive">
                  {outcome.error}
                </span>
              )}
            </span>
          )}
        </span>
      </td>
      <td className="w-16 whitespace-nowrap px-2 py-1 text-right">
        <ConfirmButton
          variant="ghost"
          size="tiny"
          isSquare
          iconSrc="paper-plane"
          content={
            row.Status === "NOT_MIGRATED"
              ? "Send this member their personal activation link."
              : "Renew this member's activation link and re-send the email. The previously emailed link stops working."
          }
          confirmContent="Send the activation email?"
          isLoading={sending}
          className={sendable ? undefined : "invisible"}
          onConfirm={() => void send()}
        />
        <TooltipButton
          variant="ghost"
          size="tiny"
          isSquare
          iconSrc="cross-small"
          content="Remove this member from the lists and from every send in this dialog. Nothing is changed on the account."
          data-feature-code="UI_MigrationCenter_MemberExclude"
          onClick={() => {
            onExclude(row.UserId)
          }}
        />
      </td>
    </tr>
  )
}

export default MigrationCenterMemberRow
